import React, { useCallback, useMemo, useState } from "react";
import { hot } from "react-hot-loader/root";
import { graphql, RelayEnvironmentProvider, useLazyLoadQuery } from "react-relay";
import { Redirect, Route, type RouteProps, useHistory, useParams } from "react-router-dom";

import * as Sentry from "@sentry/react";
import { memoize, noop } from "lodash";

import { type AppContextInterface, AppContextProvider } from "~/common/AppContext";
import { ThemeContextProvider } from "~/common/ThemeContext";
import { ActionItemsPanel } from "~/meetings/SeriesSidePanel/ActionItems/ActionItemsPanel";
import Bookmarks from "~/meetings/SeriesSidePanel/Bookmarks/Bookmarks";
import { CommentsTab } from "~/meetings/SeriesSidePanel/Comments/CommentsPanel";
import { SalesforcePanel } from "~/meetings/SeriesSidePanel/CRMPanel/Salesforce/SalesforcePanel";
import PrivateNotes from "~/meetings/SeriesSidePanel/PrivateNotes/PrivateNotesPanel";
import { useAllowedSeriesSidePanelTabs } from "~/meetings/SeriesSidePanel/SeriesSidePanel";
import { environment } from "~/models";
import Hubspot from "~/stream/SideBar/CRMPanel/Hubspot";
import {
	type CommentContext,
	StreamSideBarContext,
	type StreamSideBarContextType,
} from "~/stream/SideBar/SideBarContext";
import { ModalRoot } from "~/ui/modals";

import Notes from "./Notes";
import Tabs from "./Tabs";

import type { CompanionModeQuery } from "~/__generated__/CompanionModeQuery.graphql";

import styles from "./CompanionMode.module.css";

const companionModeQuery = graphql`
	query CompanionModeQuery($noteId: ID!) {
		...AppAccount @relay(mask: false)
		...AppUser @relay(mask: false)

		viewer {
			settings {
				theme
			}

			hasAdminAccess
			hasAccessToCopilot
			uxSimplificationEnabled
			...SeriesSidePanel_allowedTabs_viewer
			...Notes_viewer
		}

		note: node(id: $noteId) {
			__typename
			... on Note {
				id
				stream {
					id
					...SeriesSidePanel_allowedTabs_stream
					...Bookmarks_stream
				}

				...Notes_note
			}
		}
	}
`;

function warnAboutEmptyContext(functionName: string) {
	const message = `${functionName} was called but is currently noop.`;
	return () => {
		console.warn(message);
		Sentry.captureMessage(message, {
			level: "warning",
		});
	};
}

function RouteIfAllowed(props: RouteProps & { isAllowed: boolean }) {
	const params = useParams<{ noteId: string }>();

	if (!props.isAllowed) return <Redirect to={`/companion-mode/${params.noteId}/notes/`} />;

	return <Route {...props} />;
}

function CompanionMode() {
	const { noteId } = useParams<{ noteId: string }>();

	const { user, account, viewer, note } = useLazyLoadQuery<CompanionModeQuery>(companionModeQuery, {
		noteId,
	});

	const allowedTabs = useAllowedSeriesSidePanelTabs(viewer, note && note.__typename === "Note" ? note.stream : null);

	const appContextValue = React.useMemo<AppContextInterface>(
		() => ({
			user: {
				...user,
				hasAdminAccess: viewer.hasAdminAccess,
				hasAccessToCopilot: viewer.hasAccessToCopilot,
				uxSimplificationEnabled: viewer.uxSimplificationEnabled,
			},
			account,
			handleUtmAnalytics: noop,
			setSidebar: noop,
			getSidebar: () => null,
			closeSidebar: noop,
			openSettingsModal: noop,
		}),
		[user, viewer.hasAdminAccess, viewer.hasAccessToCopilot, viewer.uxSimplificationEnabled, account],
	);

	const [commentContext, _setCommentContext] = useState<CommentContext | undefined>(undefined);
	const history = useHistory();
	const goToCommentsTab = useCallback(
		(commentContext: CommentContext | undefined) => {
			if (!commentContext) {
				_setCommentContext(undefined);
				history.push(`/companion-mode/${noteId}/notes/`);
				return;
			}

			_setCommentContext(commentContext);
			history.push(`/companion-mode/${noteId}/comments/${commentContext.threadKey}`);
		},
		[history, noteId],
	);

	// TODO: We should make a new context for the new UI… most of this isn't needed or is different in the new UI.
	const sidebarContextValue = useMemo<StreamSideBarContextType>(
		() => ({
			setCommentContext: goToCommentsTab,

			// These are only included because we have them here
			currentNoteId: noteId,
			commentContext,

			// These aren't needed or are otherwise not available…
			setCurrentNoteId: warnAboutEmptyContext("setCurrentNoteId"),
			agendaBuilderCurrentNoteId: null,
			setAgendaBuilderCurrentNoteId: warnAboutEmptyContext("setAgendaBuilderCurrentNoteId"),
			agendaBuilderModeRegenerateKey: 0,
			onGenerateAgendaBuilder: warnAboutEmptyContext("onGenerateAgendaBuilder"),
			doneGeneratingAIHeaders: true,
			setDoneGeneratingAIHeaders: warnAboutEmptyContext("setDoneGeneratingAIHeaders"),
			askCopilotContext: undefined,
			setAskCopilotContext: warnAboutEmptyContext("setAskCopilotContext"),
			meetingBriefContext: undefined,
			setMeetingBriefContext: warnAboutEmptyContext("setMeetingBriefContext"),
		}),
		[commentContext, goToCommentsTab, noteId],
	);

	const makeRenderFnFor = useCallback((content: React.ReactNode) => memoize(() => content), []);

	if (!note || note.__typename !== "Note" || !note.stream) {
		// TODO: Return 404, or maybe redirect to calendar?
		return null;
	}

	return (
		<AppContextProvider value={appContextValue}>
			<ThemeContextProvider theme={viewer.settings.theme}>
				<ModalRoot>
					<StreamSideBarContext.Provider value={sidebarContextValue}>
						<div className={styles.companionMode}>
							<Tabs viewer={viewer} stream={note.stream} />
							<div className={styles.tabContent}>
								<Route
									path="/companion-mode/:noteId/notes/"
									render={makeRenderFnFor(<Notes viewer={viewer} note={note} />)}
								/>
								<Route
									path="/companion-mode/:noteId/private-notes/"
									render={makeRenderFnFor(<PrivateNotes streamId={note.stream.id} noteId={note.id} />)}
								/>
								<Route
									path="/companion-mode/:noteId/action-items/"
									render={makeRenderFnFor(<ActionItemsPanel streamId={note.stream.id} />)}
								/>
								<Route
									path="/companion-mode/:noteId/bookmarks/"
									render={makeRenderFnFor(<Bookmarks stream={note.stream} />)}
								/>
								<RouteIfAllowed
									isAllowed={allowedTabs.hubspot}
									path="/companion-mode/:noteId/hubspot/"
									render={makeRenderFnFor(
										<Hubspot streamId={note.stream.id} noteId={note.id} hasAccessToHubspotIntegration={true} />,
									)}
								/>
								<RouteIfAllowed
									isAllowed={allowedTabs.salesforce}
									path="/companion-mode/:noteId/salesforce/"
									render={makeRenderFnFor(
										<SalesforcePanel
											streamId={note.stream.id}
											noteId={note.id}
											hasAccessToSalesforceIntegration={true}
											enhancedNotesEnabled={allowedTabs.salesforceEnhancedNotesEnabled}
										/>,
									)}
								/>
								<Route path="/companion-mode/:noteId/comments/:threadKey" render={makeRenderFnFor(<CommentsTab />)} />
							</div>
						</div>
					</StreamSideBarContext.Provider>
				</ModalRoot>
			</ThemeContextProvider>
		</AppContextProvider>
	);
}

export default hot(function HotReloadingRelayWrapper() {
	return (
		<RelayEnvironmentProvider environment={environment}>
			<CompanionMode />
		</RelayEnvironmentProvider>
	);
});
